export type FlagScope = 'global' | 'tenant';

export const VERSION_OPERATORS = ['>=', '>', '<=', '<', '=='] as const;

export type VersionOperator = (typeof VERSION_OPERATORS)[number];

export const NO_GATE = '__none__';

// Verbatim from the backend's SEMVER_REGEX (src/utils/appVersions.schema.ts).
// A looser client rule turns a rejected version into a generic 400 toast
// instead of an inline field error.
export const SEMVER =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9A-Za-z-][0-9A-Za-z-]*)(?:\.(?:0|[1-9A-Za-z-][0-9A-Za-z-]*))*))?(?:\+([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?$/;

export type GateSelectValue = typeof NO_GATE | VersionOperator;

/** Radix's onValueChange hands back a bare string; narrow it without asserting. */
export function isGateSelectValue(value: string): value is GateSelectValue {
  return value === NO_GATE || (VERSION_OPERATORS as readonly string[]).includes(value);
}

// Verbatim from the backend's FEATURE_FLAG_GATE_PLATFORMS
// (src/models/FeatureFlagGates.model.ts / FeatureFlagRegistry.service.ts).
export const FEATURE_FLAG_GATE_PLATFORMS = ['ios', 'android', 'web', 'all'] as const;

export type FeatureFlagGatePlatform = (typeof FEATURE_FLAG_GATE_PLATFORMS)[number];

// Which surface a flag affects, shown as a badge so an operator knows what a
// flag touches before toggling it. Descriptive only — unlike the gate
// platforms above, this never changes who receives the flag.
export const PLATFORM_TAGS = ['web', 'mobile', 'both'] as const;

export type PlatformTag = (typeof PLATFORM_TAGS)[number];

export const PLATFORM_TAG_COPY: Record<PlatformTag, { label: string; className: string }> = {
  web: { label: 'Web', className: 'border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-300' },
  mobile: {
    label: 'Mobile',
    className: 'border-violet-500/40 bg-violet-500/10 text-violet-700 dark:text-violet-300',
  },
  both: {
    label: 'Web + Mobile',
    className: 'border-slate-500/40 bg-slate-500/10 text-slate-700 dark:text-slate-300',
  },
};

export interface FeatureFlagGate {
  id: string;
  scope: FlagScope;
  flagKey: string;
  platform: FeatureFlagGatePlatform;
  versionOp: VersionOperator | null;
  version: string | null;
  enabled: boolean;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FlagGatesResponse {
  key: string;
  gates: FeatureFlagGate[];
}

export interface UpsertFlagGateBody {
  versionOp?: VersionOperator | null;
  version?: string | null;
  enabled: boolean;
}

export interface UpsertFlagGateResponse {
  key: string;
  gate: FeatureFlagGate;
}

export interface ClearFlagGateResponse {
  key: string;
  platform: FeatureFlagGatePlatform;
}

/** A `platform='all'` gate must carry a version — otherwise it would just
 * duplicate the flag's stored value. Mirrors the backend's
 * `isGatePlatformVersionValid` so a rejected combination is an inline field
 * error instead of a generic 400 toast. */
export function isGatePlatformVersionValid(
  platform: FeatureFlagGatePlatform,
  versionOp: VersionOperator | typeof NO_GATE
): boolean {
  return platform !== 'all' || versionOp !== NO_GATE;
}

export interface FeatureFlagDefinition {
  scope: FlagScope;
  key: string;
  label: string;
  description: string | null;
  platformTag: PlatformTag;
  defaultEnabled: boolean;
  isBuiltIn: boolean;
  isArchived: boolean;
  /** Derived by the backend from a hardcoded guard-site list — never an
   * admin-settable field. Enforced flags block traffic when off and reject
   * gate writes; see `ENFORCED_FLAG_DISABLE_COPY` below. */
  enforced: boolean;
  /** Whether identity rules can be added. Absent from a backend that predates it, where
   * only advisory flags accepted rules — read it through `acceptsTargeting`. */
  targetable?: boolean;
  /** How many identity rules target this flag. Comes off the list response so
   * the row can badge targeting without a rules fetch per flag. */
  ruleCount: number;
  createdBy: string | null;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string;
}

export type FeatureFlagMap = Record<string, boolean>;

export interface FeatureFlagsResponse {
  flags: FeatureFlagMap;
  definitions: FeatureFlagDefinition[];
}

export interface UpdateFeatureFlagResponse {
  key: string;
  enabled: boolean;
}

export interface CreateFeatureFlagBody {
  key: string;
  label: string;
  description?: string | null;
  platformTag?: PlatformTag;
  defaultEnabled?: boolean;
}

export interface UpdateFeatureFlagBody {
  enabled?: boolean;
  label?: string;
  description?: string | null;
  platformTag?: PlatformTag;
  defaultEnabled?: boolean;
}

export interface ArchiveFeatureFlagResponse {
  key: string;
  isArchived: boolean;
}

/** Confirmation on turning a flag ON — for advisory flags whose UI effect is
 * severe even though the backend enforces nothing (e.g. the mobile kill
 * switch). Independent of `ENFORCED_FLAG_DISABLE_COPY` below: this guards the
 * *enable* direction, that one guards the *disable* direction, and today's
 * data never needs both on the same key. */
export const FLAG_CONFIRM_ON_ENABLE: Record<string, { title: string; description: string }> = {
  appKillSwitchEnabled: {
    title: 'Shut down the mobile app?',
    description:
      'Are you sure you want to disable the mobile app and redirect users to the website?',
  },
};

/** Confirmation on turning an *enforced* flag OFF — names the concrete blast
 * radius rather than a generic "are you sure?" (FT-4716). Deliberately
 * frontend-owned rather than a backend field: `enforced` (boolean) is
 * derived from the backend so the badge and gate-control visibility can
 * never drift, but this copy is editorial and change-managed alongside the
 * dashboard, same as `FLAG_CONFIRM_ON_ENABLE`. Keep in sync with the backend
 * guard set in `FeatureFlagRegistry.service.ts` (`isEnforcedFlag`). */
export const ENFORCED_FLAG_DISABLE_COPY: Record<string, string> = {
  groupOrdering:
    '403s all group-order endpoints, including invite links already sent. In-flight orders cannot be closed or cancelled, and the expiry sweep that would clean them up also stops.',
  favorites: '403s the favorites routes and returns isFavorite: false on every restaurant.',
  birthdayCollection: 'rejects date-of-birth writes and suppresses the birthday prompt.',
  basketItemLimits:
    'stops enforcing the per-bag and per-group-order item limits; bags and group orders go back to the Olo maximum of 999.',
};

const ENFORCED_FLAG_DISABLE_GENERIC_COPY =
  'blocks backend requests for this feature immediately — the exact effect is not yet documented here.';

/** Never returns undefined for an enforced flag — an enforced key missing
 * from the map above still gets a dialog, just a generic one. */
export function getEnforcedFlagDisableCopy(key: string): string {
  return ENFORCED_FLAG_DISABLE_COPY[key] ?? ENFORCED_FLAG_DISABLE_GENERIC_COPY;
}

// Verbatim from the backend's EMAIL_MATCH_MODES
// (src/services/FeatureFlagRegistry.service.ts).
export const EMAIL_MATCH_MODES = ['exact', 'contains'] as const;

export type EmailMatchMode = (typeof EMAIL_MATCH_MODES)[number];

export interface FeatureFlagRule {
  id: string;
  scope: FlagScope;
  tenantKey: string | null;
  flagKey: string;
  /** The value the flag takes for everyone this rule names. Unlike a gate, a
   * rule can turn a flag ON above its stored value. */
  enabled: boolean;
  userIds: string[] | null;
  emails: string[] | null;
  emailMatch: EmailMatchMode;
  isActive: boolean;
  note: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string;
}

export interface FlagRulesResponse {
  key: string;
  rules: FeatureFlagRule[];
}

export interface FlagRuleResponse {
  key: string;
  rule: FeatureFlagRule;
}

export interface DeleteFlagRuleResponse {
  key: string;
  ruleId: string;
}

export interface FlagRuleBody {
  enabled: boolean;
  userIds?: string[] | null;
  emails?: string[] | null;
  emailMatch?: EmailMatchMode;
  isActive?: boolean;
  note?: string | null;
}

export type UpdateFlagRuleBody = Partial<FlagRuleBody>;

/** The operator picks one of three kinds, and the kind IS the rule's position
 * in the chain — the backend evaluates subjects, then exact emails, then
 * fragments. Collapsing (axis + match mode) into one choice is what keeps the
 * fixed order legible without a priority field to mislead anyone. */
export const RULE_KINDS = ['subject', 'exactEmail', 'containsEmail'] as const;

export type RuleKind = (typeof RULE_KINDS)[number];

export function isRuleKind(value: string): value is RuleKind {
  return (RULE_KINDS as readonly string[]).includes(value);
}

export const RULE_KIND_COPY: Record<
  RuleKind,
  { option: string; tier: string; help: string; valueLabel: string; placeholder: string; reason: string }
> = {
  subject: {
    option: 'Specific people, by user ID',
    tier: 'Subject',
    help: 'Cognito subject values. The most precise rule type, so it is checked first.',
    valueLabel: 'User IDs',
    placeholder: 'sub-8f2a4c1e',
    reason: 'subject rules are always checked before email rules.',
  },
  exactEmail: {
    option: 'An exact email address',
    tier: 'Email · exact',
    help: 'Matches the whole address, ignoring case.',
    valueLabel: 'Email addresses',
    placeholder: 'qa@moxielabs.co',
    reason: 'an exact address beats a fragment match.',
  },
  containsEmail: {
    option: 'Any email containing a fragment',
    tier: 'Email · contains',
    help: 'Use it to target a whole domain. Broad — it matches anyone whose address contains the text.',
    valueLabel: 'Fragments',
    placeholder: '@moxielabs.co',
    reason: 'fragment matches are the broadest, so they are checked last.',
  },
};

/** Evaluation rank, mirroring the backend's ruleRank. Rules arrive ordered, so
 * this is only needed to place a rule being edited. */
export const RULE_KIND_RANK: Record<RuleKind, number> = {
  subject: 0,
  exactEmail: 1,
  containsEmail: 2,
};

export function ruleKindOf(rule: Pick<FeatureFlagRule, 'userIds' | 'emailMatch'>): RuleKind {
  if (rule.userIds !== null && rule.userIds.length > 0) return 'subject';
  return rule.emailMatch === 'exact' ? 'exactEmail' : 'containsEmail';
}

/** Splits a textarea into entries the way the backend normalizes them. */
export function splitRuleEntries(value: string): string[] {
  const seen: string[] = [];
  for (const line of value.split('\n')) {
    const entry = line.trim();
    if (entry.length > 0 && !seen.includes(entry)) seen.push(entry);
  }
  return seen;
}

/** Plain-English condition text for a rule row. */
export function describeRule(rule: FeatureFlagRule): string {
  const kind = ruleKindOf(rule);
  if (kind === 'subject') {
    const count = rule.userIds?.length ?? 0;
    return count === 1 ? '1 user ID' : `${count} user IDs`;
  }
  const entries = rule.emails ?? [];
  if (kind === 'containsEmail') {
    return entries.length === 1
      ? `Email contains ${entries[0]}`
      : `Email contains any of ${entries.length}`;
  }
  return entries.length === 1 ? entries[0] : `${entries.length} email addresses`;
}

/** Whether the backend accepts identity rules on this flag. */
export function acceptsTargeting(definition: Pick<FeatureFlagDefinition, 'enforced' | 'targetable'>): boolean {
  return definition.targetable ?? !definition.enforced;
}
